/* SpawnAnything — shareable clips: the last ~15 s of a battle as a vertical (9:16) video for TikTok/Shorts/Reels.
   The clip is re-rendered from the replay, not grabbed from the player's screen: a second sim worker starts from the
   nearest state snapshot (or the setup) at most 15 s before the end, fast-forwards to the clip start and then plays
   the clip in real time while main.js renders it with the kino camera at 1080×1920 (720×1280 on weak devices).
   Each rendered frame is copied into a 2D canvas with the watermark and recorded with canvas.captureStream(30) +
   MediaRecorder (MP4/H.264 where supported, else WebM VP9/VP8). The live battle pauses meanwhile and continues
   unchanged afterwards (its worker is untouched). `game` is the adapter from main.js. */
import { downloadBlob, canShareFile, shareFile, fmtBytes } from './share.js';
import { replayFileName } from '../replay/format.js';

export const CLIP_TICKS = 450; // 15 s at 30 ticks/s
/** Auto clip (user decision 19A): the 15 s with the most highlight weight (slowmo highlight log, game seconds; t1 = last
    touch) up to `end` ticks; the decisive end of the battle (overAt) counts as a highlight too. A clip starts ~2 s before
    its first highlight. Without highlights: the last 15 s. Returns [T0, T1] in ticks. */
export function bestWindow(hl, end, overAt = 0, len = CLIP_TICKS) {
  const pts = (hl || []).map(h => ({ a: Math.round(h.t * 30), b: Math.round((h.t1 ?? h.t) * 30), w: h.w || 1 })).filter(p => p.a <= end);
  if (overAt > 0 && overAt <= end) pts.push({ a: overAt, b: overAt, w: 3 });
  let best = [Math.max(0, end - len), end], bw = -1;
  for (const p of pts) {
    const T0 = Math.max(0, Math.min(p.a - 60, end - len)), T1 = Math.min(end, T0 + len);
    let w = 0; for (const q of pts) if (q.b >= T0 && q.a <= T1) w += q.w;
    if (w > bw + 1e-9) { bw = w; best = [T0, T1]; } // ties: the first window, with the lead-in before its highlight
  }
  return best;
}
const MIMES = ['video/mp4;codecs=avc1.640028', 'video/mp4;codecs=avc1.4d0028', 'video/mp4;codecs=avc1', 'video/mp4',
  'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
export function pickMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIMES) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) { /* next */ } }
  return '';
}
const supported = () => typeof MediaRecorder !== 'undefined' && typeof HTMLCanvasElement !== 'undefined' && !!HTMLCanvasElement.prototype.captureStream;
const el = html => { const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; };
const frames = n => new Promise(r => { const f = () => (--n <= 0 ? r() : requestAnimationFrame(f)); requestAnimationFrame(f); });
const CANCEL = Symbol('cancel');
const TEAM_CSS = ['#7fb0ff', '#ff8a7a'], TEAM_NAME = ['Blau', 'Rot'];

export function createClipper(game) {
  let busy = false, job = null, resultUrl = null;
  const ov = el(`<div class="aw-clipov" hidden>
    <div class="aw-clipstage"></div>
    <div class="aw-clippanel" role="dialog" aria-modal="true" aria-label="Clip">
      <b class="aw-cliptitle">Clip</b>
      <p class="aw-clipsub"></p>
      <div class="aw-clipbar"><i></i></div>
      <div class="aw-clipbtns"></div>
    </div>
  </div>`);
  document.body.append(ov);
  const q = s => ov.querySelector(s);

  function ui(title, sub, p, buttons) {
    ov.hidden = false;
    q('.aw-cliptitle').textContent = title; q('.aw-clipsub').textContent = sub || '';
    q('.aw-clipbar').hidden = p == null; if (p != null) q('.aw-clipbar i').style.width = Math.round(Math.max(0, Math.min(1, p)) * 100) + '%';
    if (buttons) {
      const box = q('.aw-clipbtns'); box.replaceChildren();
      for (const [label, fn, cls] of buttons) { const b = el(`<button class="${cls || ''}"></button>`); b.textContent = label; b.addEventListener('click', fn); box.append(b); }
    }
  }
  function closeUi() {
    ov.hidden = true; q('.aw-clipstage').replaceChildren();
    if (resultUrl) { URL.revokeObjectURL(resultUrl); resultUrl = null; }
  }
  addEventListener('keydown', e => { if (!ov.hidden && e.code === 'Escape') { e.preventDefault(); if (busy) cancel(); else closeUi(); } }, true);
  function cancel() { if (job) { job.cancelled = true; if (job.reject) job.reject(CANCEL); } }

  async function start() {
    if (busy) return;
    if (!ov.hidden) closeUi();
    const mime = pickMime();
    if (!supported() || mime === null) { game.toast('Dieser Browser kann keine Videos aufnehmen.'); return; }
    const src = game.clipSource();
    if (!src || src.end < 30) { game.toast('Noch zu wenig aufgenommen für einen Clip.'); return; }
    busy = true; job = { cancelled: false, reject: null };
    const J = job, [T0, T1] = src.window || [Math.max(0, src.end - CLIP_TICKS), src.end], title = src.rep.title || game.title();
    const weak = game.coarse || (navigator.hardwareConcurrency || 4) < 4;
    const W = weak ? 720 : 1080, H = weak ? 1280 : 1920, bps = weak ? 5e6 : 8e6;
    const wasPaused = game.paused;
    let restore = null, off = null, rec = null, stream = null, desync = false, blob = null, t0 = 0, cleanup = null;
    const cancelBtn = [['Abbrechen', cancel, 'ghost']];
    ui('Clip wird vorbereitet …', title, 0, cancelBtn);
    try {
      game.setPaused(true);
      const snap = await game.requestSnap(T0);
      if (J.cancelled) throw CANCEL;
      // a second worker replays the battle; the live one stays paused and untouched
      const w = game.makeWorker();
      restore = game.swapWorker(w);
      let handler = null;
      off = game.listen(m => { if (m.type === 'rpdesync') desync = true; if (handler) handler(m); });
      const wait = (type, other) => new Promise((resolve, reject) => {
        J.reject = reject;
        handler = m => { if (m.type === type) { handler = null; resolve(m); } else if (other) other(m); };
      });
      const s0 = snap ? snap.stepN : 0;
      const ready = wait(T0 > s0 ? 'rpseeked' : 'frame', m => { if (m.type === 'rpprog') ui('Clip wird vorbereitet …', title, (m.step - s0) / Math.max(1, T0 - s0)); });
      w.postMessage(game.setupMsg(src.rep));
      w.postMessage({ cmd: 'replay', cmds: src.rep.cmds, hashes: src.rep.hashes, end: T1, snap, seek: T0, view: 1 });
      await ready;
      if (document.fonts && document.fonts.load) await Promise.race([document.fonts.load(`${Math.round(H * 0.04)}px Bungee`), frames(20)]).catch(() => {});

      // render target: the game canvas at W×H, copied with the watermark into `comp`, which is recorded
      const comp = document.createElement('canvas'); comp.width = W; comp.height = H;
      const ctx = comp.getContext('2d');
      const lines = wrap(ctx, title, W * 0.88, Math.round(W * 0.075));
      let step = T0;
      game.captureBegin(W, H, () => draw(ctx, game.canvas, W, H, lines, game.info(), Math.max(0, (step - (T1 - 40)) / 40)));
      comp.className = 'aw-clipvideo'; q('.aw-clipstage').replaceChildren(comp); // preview = exactly what is recorded
      await frames(3);
      stream = comp.captureStream(30);
      rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: bps });
      const chunks = [];
      rec.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
      const stopped = new Promise(r => { rec.onstop = r; });
      rec.start(500); t0 = performance.now();
      // a hidden tab gets no animation frames: the video would stall, so stop instead
      const onHide = () => { if (document.hidden) { game.toast('Clip abgebrochen: Das Fenster war im Hintergrund.'); cancel(); } };
      document.addEventListener('visibilitychange', onHide); cleanup = () => document.removeEventListener('visibilitychange', onHide);
      const done = wait('rpend', m => { if (m.type === 'frame' && m.step != null) { step = m.step; ui('Aufnahme läuft …', `${title} · ${W}×${H}`, (step - T0) / Math.max(1, T1 - T0)); } });
      w.postMessage({ cmd: 'run', running: true, timeScale: 1 });
      await done;
      step = T1;
      await frames(4);
      rec.stop(); await stopped;
      blob = new Blob(chunks, { type: (rec.mimeType || mime || 'video/webm').split(';')[0] });
    } catch (e) {
      if (e !== CANCEL) { console.error(e); game.toast('Der Clip konnte nicht erstellt werden.'); }
      if (rec && rec.state !== 'inactive') try { rec.stop(); } catch (err) { /* ignore */ }
    } finally {
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (cleanup) cleanup();
      game.captureEnd();
      if (off) off();
      if (restore) restore();
      game.setPaused(wasPaused);
      busy = false; job = null;
    }
    if (!blob || !blob.size) { closeUi(); return; }
    const secs = (performance.now() - t0) / 1000;
    console.info(`[clip] ${blob.type} ${W}×${H}, ${fmtBytes(blob.size)}, recorded in ${secs.toFixed(1)} s`);
    showResult(blob, title, W, H, desync);
  }

  function showResult(blob, title, W, H, desync) {
    const ext = blob.type.includes('mp4') ? '.mp4' : '.webm', name = replayFileName(title, ext);
    const file = new File([blob], name, { type: blob.type });
    resultUrl = URL.createObjectURL(blob);
    const v = el(`<video class="aw-clipvideo" controls loop muted playsinline autoplay></video>`);
    v.src = resultUrl;
    q('.aw-clipstage').replaceChildren(v);
    const btns = [['Herunterladen', () => downloadBlob(file, name), 'go']];
    if (canShareFile(file)) btns.push(['Teilen', async () => { try { await shareFile(file, title, `${title} – SpawnAnything`); } catch (e) { game.toast('Teilen ist fehlgeschlagen.'); } }, 'go']);
    btns.push(['Schließen', closeUi, 'ghost']);
    ui('Dein Clip ist fertig', `${ext.slice(1).toUpperCase()} · ${W}×${H} · ${fmtBytes(blob.size)}${desync ? ' · Achtung: weicht vom Spiel ab' : ''}`, null, btns);
  }

  return { start, get busy() { return busy; } };
}

/* ---- watermark ---- */
function wrap(ctx, text, maxW, px) {
  ctx.font = `${px}px Bungee, Impact, sans-serif`;
  const out = [];
  // "10.000 Hühner vs. 1 T-Rex": each side on its own line(s) unless everything fits on one
  const one = String(text), parts = ctx.measureText(one).width <= maxW ? [one] : one.split(/\s+(?=vs\.\s)/);
  for (const part of parts) {
    let cur = '';
    for (const w of part.split(/\s+/)) { const t = cur ? cur + ' ' + w : w; if (cur && ctx.measureText(t).width > maxW) { out.push(cur); cur = w; } else cur = t; }
    if (cur) out.push(cur);
  }
  // still too wide (one long word) or too many lines: shrink
  const widest = Math.max(...out.map(l => ctx.measureText(l).width));
  const k = Math.min(1, maxW / widest, out.length > 3 ? 3 / out.length : 1);
  return { lines: out, px: Math.max(12, Math.floor(px * k)) };
}
function draw(ctx, src, W, H, T, info, endK) {
  ctx.drawImage(src, 0, 0, W, H);
  const s = W / 1080;
  // top band: logo, matchup, unit counts (kept clear of the TikTok/Reels UI at the bottom and right edge)
  let g = ctx.createLinearGradient(0, 0, 0, H * 0.3);
  g.addColorStop(0, 'rgba(6,9,14,0.62)'); g.addColorStop(1, 'rgba(6,9,14,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H * 0.3);
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 5 * s;
  ctx.font = `${Math.round(40 * s)}px Bungee, Impact, sans-serif`; ctx.fillStyle = '#ffc93c';
  ctx.fillText('SPAWN ANYTHING', W / 2, 118 * s);
  ctx.font = `${T.px}px Bungee, Impact, sans-serif`; ctx.fillStyle = '#ffffff';
  let y = 118 * s + T.px * 1.35;
  for (const l of T.lines) { ctx.fillText(l, W / 2, y); y += T.px * 1.08; }
  const al = (info && info.alive) || [0, 0];
  ctx.font = `700 ${Math.round(54 * s)}px "Barlow Semi Condensed", "Arial Narrow", sans-serif`;
  const a = `${TEAM_NAME[0]} ${al[0].toLocaleString('de-DE')}`, b = `${al[1].toLocaleString('de-DE')} ${TEAM_NAME[1]}`, mid = '  ·  ';
  const wa = ctx.measureText(a).width, wm = ctx.measureText(mid).width, wb = ctx.measureText(b).width;
  let x = W / 2 - (wa + wm + wb) / 2; y += 16 * s;
  ctx.textAlign = 'left';
  ctx.fillStyle = TEAM_CSS[0]; ctx.fillText(a, x, y); x += wa;
  ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.fillText(mid, x, y); x += wm;
  ctx.fillStyle = TEAM_CSS[1]; ctx.fillText(b, x, y);
  // end card: the winner
  if (endK > 0 && info && info.over) {
    const k = Math.min(1, endK);
    ctx.fillStyle = `rgba(6,9,14,${0.45 * k})`; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center'; ctx.globalAlpha = k;
    ctx.font = `${Math.round(96 * s)}px Bungee, Impact, sans-serif`;
    ctx.fillStyle = info.winner === 0 ? TEAM_CSS[0] : info.winner === 1 ? TEAM_CSS[1] : '#ffffff';
    ctx.fillText(info.winner === 0 ? 'Blau gewinnt!' : info.winner === 1 ? 'Rot gewinnt!' : 'Unentschieden', W / 2, H * 0.52);
    ctx.globalAlpha = 1;
  }
  ctx.shadowOffsetY = 0;
}
